// src/utils/credentialFormat.js
//
// Shopvia products describe their own field order in the product description,
// e.g. "FORMAT UID|PASS|2FA" or "format uid|pass|email|pass email|cookie".
// This turns that into readable labels instead of a raw "a|b|c" string.
// If we can't confidently parse it, we fall back gracefully — never throws,
// never shows something worse than the current raw line.

function labelForToken(tok) {
  const t = (tok || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  if (["uid", "id", "user", "username", "login"].includes(t)) return "Username / Login";
  if (["pass", "password"].includes(t)) return "Password";
  if (t.includes("2fa")) return "2FA / Secret Key";
  if (t.includes("passemail") || t.includes("emailpass") || (t.includes("email") && t.includes("pass"))) return "Email Password";
  if (t === "email" || t === "mail") return "Email";
  if (t.includes("recover")) return "Recovery Email";
  if (t === "cookie") return "Cookie";
  if (t.includes("friend")) return "Friends";
  if (t === "gender") return "Gender";
  if (t.includes("birth") || t === "dob") return "Date of Birth";
  if (t.includes("creat")) return "Date Created";
  if (t.includes("refreshtoken")) return "Refresh Token";
  if (t.includes("clientid")) return "Client ID";
  if (t === "country") return "Country";
  if (t === "info") return "Info";
  return null;
}

function extractFormatLabels(description) {
  if (!description) return null;
  const match = description.match(/f[oe]?rmat\w*[\s:;.\-]*([a-z0-9_ ]+(?:\|[a-z0-9_ ]+)+)/i);
  if (!match) return null;
  const tokens = match[1].split("|").map((t) => t.trim()).filter(Boolean);
  if (tokens.length < 2) return null;
  return tokens.map(labelForToken);
}

const DEFAULT_LABELS_BY_COUNT = {
  1: ["Credential"],
  2: ["Username / Login", "Password"],
  3: ["Username / Login", "Password", "2FA / Secret Key"],
  4: ["Username / Login", "Password", "2FA / Secret Key", "Email"],
  5: ["Username / Login", "Password", "2FA / Secret Key", "Email", "Email Password"],
};

// Returns { type: "labeled", rows: [{label, value}] } or { type: "raw", text }.
export function formatShopviaCredential(rawLine, description) {
  const line = (rawLine || "").trim();
  if (!line.includes("|")) return { type: "raw", text: line };

  const values = line.split("|").map((v) => v.trim());
  let labels = extractFormatLabels(description);

  if (!labels || labels.some((l) => l === null) || labels.length !== values.length) {
    labels = DEFAULT_LABELS_BY_COUNT[values.length] || null;
  }
  if (!labels) return { type: "raw", text: line };

  return {
    type: "labeled",
    rows: values.map((value, i) => ({ label: labels[i] || `Extra Info ${i + 1}`, value })),
  };
}

export function credentialRowsToText(rows) {
  return rows.map((r) => `${r.label}: ${r.value}`).join("\n");
}